/* Finora cart-aware UX. Native Shopify remains the only cart/discount writer. */
(function () {
  'use strict';
  const MATTE_HANDLES = new Set(['arabica-lipstick', 'super-red-lipstick', 'caramel-whisper-lipstick', 'sienna-glow-lipstick', 'crockery-tint-lipstick', 'brunette-rose-lipstick']);
  function variantQuantity(cart, id) {
    return (cart?.items || []).reduce((n, item) => n + (String(item.variant_id || item.id) === String(id) ? Number(item.quantity) || 0 : 0), 0);
  }
  function productPresent(cart, id) {
    return (cart?.items || []).some(item => String(item.product_id) === String(id) && Number(item.quantity) > 0);
  }
  const cartSignature = value => JSON.stringify((value?.items || []).map(item => [String(item.variant_id || item.id), Number(item.quantity) || 0]).sort());
  function matteQuantity(cart) {
    return (cart?.items || []).reduce((n, item) => n + (MATTE_HANDLES.has(item.handle) ? Number(item.quantity) || 0 : 0), 0);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { variantQuantity, productPresent, matteQuantity };
  if (typeof window === 'undefined' || window.FinoraCartUX) return;
  let cart = null, refreshing = null, busy = false, rendering = 0, pending = null;
  let activeDialog = null, dialogResolve = null, returnFocus = null;
  const allowed = new WeakSet();
  const buttonState = new WeakMap();
  const cfg = () => document.getElementById('finora-cart-state');
  const cartURL = () => cfg()?.dataset.cartUrl || ((window.Shopify?.routes?.root || '/') + 'cart');
  const text = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const forms = () => [...document.querySelectorAll('product-form-component form')].filter(form => /\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action, location.href).pathname));
  const formVariant = form => form.querySelector('[name="id"]')?.value || '';
  const formButton = form => form.querySelector('button[name="add"], button[type="submit"]');
  function readSeed() {
    try { const data = JSON.parse(cfg()?.textContent || 'null'); if (Array.isArray(data?.items)) cart = data; } catch (_) { /* A fresh native read will be attempted. */ }
  }
  function stateFor(button) {
    if (!buttonState.has(button)) buttonState.set(button, { unavailable: button.disabled, original: button.textContent.trim() });
    return buttonState.get(button);
  }
  function announce(message) {
    const node = document.querySelector('[data-finora-cart-message]');
    if (node) { text(node, message); node.hidden = !message; }
  }
  function paint() {
    if (!cart) return;
    for (const form of forms()) {
      const button = formButton(form); if (!button) continue;
      const state = stateFor(button), qty = variantQuantity(cart, formVariant(form));
      const isMain = button.id === 'fb-product-add';
      const isFinora = isMain || button.classList.contains('fj-add');
      if (!button.hasAttribute('data-finora-busy')) state.unavailable = button.disabled;
      button.toggleAttribute('data-finora-busy', busy);
      button.disabled = state.unavailable || busy;
      if (isFinora) {
        const rawPrice = button.dataset.finoraPrice || (isMain ? document.querySelector('.fbp #pr')?.textContent.trim() : '');
        // Shopify money formatting may include an HTML wrapper. Never expose it in button text.
        if (state.priceMarkup !== rawPrice) {
          const priceTemplate = document.createElement('template');
          priceTemplate.innerHTML = String(rawPrice || '');
          state.plainPrice = priceTemplate.content.textContent.trim();
          state.priceMarkup = rawPrice;
        }
        const price = state.plainPrice;
        const first = price ? 'ADD TO CART · ' + price : state.original;
        text(button, state.unavailable ? 'SOLD OUT' : busy ? 'PLEASE WAIT…' : qty > 0 ? 'ADD ONE MORE' : first);
        button.dataset.finoraQuantity = String(qty);
        if (isMain) {
          const note = document.querySelector('[data-finora-in-cart]');
          if (note) { note.hidden = qty < 1; text(note.querySelector('[data-finora-count]'), qty + ' IN YOUR CART'); }
        }
      }
    }
    const main = document.getElementById('fb-product-add');
    const sticky = document.querySelector('#fb-sticky-buy button');
    if (main && sticky) {
      text(sticky, main.textContent);
      sticky.disabled = main.disabled;
      const qty = Number(main.dataset.finoraQuantity || 0);
      text(document.querySelector('#fb-sticky-buy .fb-copy span'), qty > 0 ? qty + ' IN YOUR CART' : document.querySelector('.fbp #pr')?.textContent.trim() || '');
    }
    document.querySelectorAll('[data-finora-recommendation]').forEach(card => {
      card.hidden = productPresent(cart, card.dataset.productId);
    });
    document.querySelectorAll('[data-finora-pdp-next]').forEach(section => {
      const count = [...section.querySelectorAll('[data-finora-recommendation]')].filter(card => !card.hidden).length;
      section.dataset.recommendationCount = String(count);
      section.querySelector('[data-finora-upsell-heading]')?.toggleAttribute('hidden', count === 0);
      section.querySelector('.fbp-next-grid')?.toggleAttribute('hidden', count === 0);
      section.querySelector('[data-finora-bag-ready]')?.toggleAttribute('hidden', count !== 0 || !cart.item_count);
      section.hidden = count === 0 && !cart.item_count;
    });
    document.dispatchEvent(new CustomEvent('finora:cart-painted'));
  }
  async function freshCart(force = false) {
    if (force && refreshing) { try { await refreshing; } catch (_) {} }
    if (refreshing) return refreshing;
    refreshing = (async () => {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(cartURL() + '.js', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Cart read failed');
        const data = await response.json();
        if (!Array.isArray(data.items)) throw new Error('Invalid cart response');
        const changed = cart && cartSignature(cart) !== cartSignature(data);
        cart = data; paint();
        if (changed && !pending) document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: data, sourceId: 'finora-cart-aware', data: { source: 'finora-cart-aware' } } }));
        return data;
      } finally { clearTimeout(timeout); }
    })();
    try { return await refreshing; } finally { refreshing = null; }
  }
  async function refreshPdpRecommendations() {
    const current = document.querySelector('[data-finora-pdp-next]');
    if (!current?.dataset.sectionId) return;
    const revision = ++rendering, url = new URL(location.href);
    url.searchParams.delete('section_id'); url.searchParams.set('sections', current.dataset.sectionId);
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Section read failed');
      const sections = await response.json(), html = sections[current.dataset.sectionId];
      if (typeof html !== 'string') throw new Error('Section missing');
      const updated = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-finora-pdp-next]');
      if (!updated || revision !== rendering || !current.isConnected) return;
      const focusWasInside = current.contains(document.activeElement);
      current.replaceWith(updated); paint();
      if (focusWasInside) updated.querySelector('a:not([hidden])')?.focus({ preventScroll: true });
    } catch (_) { paint(); /* Never re-show a known in-cart recommendation. */ }
    finally { clearTimeout(timeout); }
  }
  async function refreshAll() {
    try { await freshCart(); await refreshPdpRecommendations(); } catch (_) { /* Pre-add verification remains fail-closed. */ }
  }
  function finishDialog(confirmed) {
    const resolve = dialogResolve; dialogResolve = null;
    if (activeDialog?.open) activeDialog.close();
    const focus = returnFocus; returnFocus = null;
    if (focus?.isConnected && !focus.disabled) focus.focus({ preventScroll: true });
    if (resolve) resolve(confirmed);
  }
  function confirmRepeat(quantity, addQuantity, trigger) {
    if (!activeDialog) {
      activeDialog = document.createElement('dialog'); activeDialog.className = 'finora-repeat-dialog';
      activeDialog.setAttribute('aria-labelledby', 'finora-repeat-title');
      activeDialog.setAttribute('aria-describedby', 'finora-repeat-description');
      activeDialog.innerHTML = '<p class="finora-repeat-ey" id="finora-repeat-title"></p><p id="finora-repeat-description"></p><div class="finora-repeat-actions"><button type="button" data-finora-confirm>ADD ANOTHER</button><button type="button" data-finora-keep>KEEP ONE</button></div>';
      document.body.appendChild(activeDialog);
      activeDialog.querySelector('[data-finora-confirm]').addEventListener('click', () => finishDialog(true));
      activeDialog.querySelector('[data-finora-keep]').addEventListener('click', () => finishDialog(false));
      activeDialog.addEventListener('cancel', event => { event.preventDefault(); finishDialog(false); });
      activeDialog.addEventListener('click', event => {
        if (event.target !== activeDialog) return;
        const r = activeDialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) finishDialog(false);
      });
    }
    text(activeDialog.querySelector('#finora-repeat-title'), quantity + ' IN YOUR CART');
    text(activeDialog.querySelector('#finora-repeat-description'), quantity === 1 ? 'You already have one in your cart. Need another?' : 'You already have ' + quantity + ' in your cart. Need another?');
    text(activeDialog.querySelector('[data-finora-confirm]'), addQuantity === 1 ? 'ADD ANOTHER' : 'ADD ' + addQuantity + ' MORE');
    text(activeDialog.querySelector('[data-finora-keep]'), quantity === 1 ? 'KEEP ONE' : 'KEEP ' + quantity);
    returnFocus = trigger;
    return new Promise(resolve => {
      dialogResolve = resolve;
      if (typeof activeDialog.showModal !== 'function') { finishDialog(false); announce('Please review your bag to add another.'); return; }
      activeDialog.showModal(); activeDialog.querySelector('[data-finora-keep]').focus();
    });
  }
  async function nativeAdd(form, submitter) {
    return new Promise((resolve, reject) => {
      let timer;
      const cleanup = () => { clearTimeout(timer); document.removeEventListener('cart:update', success); document.removeEventListener('cart:error', failure); pending = null; };
      const success = event => {
        if (event.detail?.data?.source !== 'product-form-component' || String(event.detail.data.productId) !== String(form.closest('product-form-component')?.dataset.productId)) return;
        cleanup(); event.detail.data.didError ? reject(new Error('The addition was not completed. Please review your bag.')) : resolve();
      };
      const failure = () => { cleanup(); reject(new Error('The item could not be added. Please review your bag.')); };
      document.addEventListener('cart:update', success); document.addEventListener('cart:error', failure);
      pending = { form };
      timer = setTimeout(() => { cleanup(); reject(new Error('The addition could not be confirmed. Please review your bag before trying again.')); }, 15000);
      try {
        // Re-enable only for the synchronous native handler, then lock every add control again.
        for (const f of forms()) { const b = formButton(f); if (b) b.disabled = stateFor(b).unavailable; }
        allowed.add(form);
        if (submitter?.isConnected && submitter.form === form) form.requestSubmit(submitter); else form.requestSubmit();
        allowed.delete(form); paint();
      } catch (error) { allowed.delete(form); cleanup(); reject(error); }
    });
  }
  async function handleIntent(form, submitter) {
    if (busy || !form.isConnected || !form.checkValidity()) return;
    const button = formButton(form); if (!button || stateFor(button).unavailable) return;
    const focusOrigin = document.activeElement?.closest?.('#fb-sticky-buy') ? document.activeElement : (submitter || button);
    busy = true; announce(''); paint();
    try {
      let latest = await freshCart(true);
      const variant = formVariant(form), qtyToAdd = Math.max(1, Number(new FormData(form).get('quantity')) || 1);
      let existing = variantQuantity(latest, variant);
      if (!variant) throw new Error('Choose your product before adding.');
      if (form.closest('[data-offer-third="true"]') && matteQuantity(latest) !== 2) {
        await refreshPdpRecommendations();
        throw new Error('Your bag changed. Review the updated offer before adding.');
      }
      if (existing > 0) {
        const confirmed = await confirmRepeat(existing, qtyToAdd, focusOrigin);
        if (!confirmed) return;
        // Re-check after the customer has had time to decide. A changed count requires renewed consent.
        latest = await freshCart(true);
        const now = variantQuantity(latest, variant);
        if (formVariant(form) !== variant) throw new Error('Your selection changed. Please review it before adding.');
        if (now > 0 && now !== existing && !await confirmRepeat(now, qtyToAdd, focusOrigin)) return;
      }
      if (!form.isConnected) throw new Error('Your bag changed. Please use the updated product card.');
      await nativeAdd(form, submitter);
      await freshCart(true);
      await refreshPdpRecommendations();
    } catch (error) {
      announce(error?.message && !/fetch|abort|Cart read|Invalid cart/i.test(error.message) ? error.message : 'We could not refresh your bag. Please review it before adding.');
      try { await freshCart(); } catch (_) { /* Do not retry the add. */ }
    } finally {
      busy = false; paint();
      if (focusOrigin?.isConnected && !focusOrigin.disabled && document.activeElement === document.body) focusOrigin.focus({ preventScroll: true });
    }
  }
  // Horizon delegates submit in DOCUMENT capture. WINDOW capture must guard it first.
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !forms().includes(form)) return;
    if (allowed.has(form)) { allowed.delete(form); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    void handleIntent(form, event.submitter);
  }, true);
  document.addEventListener('cart:update', event => {
    if (Array.isArray(event.detail?.resource?.items)) { cart = event.detail.resource; paint(); }
    if (!pending) void refreshAll();
    requestAnimationFrame(paint);
  });
  document.addEventListener('discount:update', () => { void refreshAll(); });
  document.addEventListener('finora:controls-ready', paint);
  document.addEventListener('variant:update', event => {
    const main = document.getElementById('fb-product-add');
    if (main && event.detail?.resource && String(event.detail.data?.productId) === main.dataset.finoraProductId) {
      stateFor(main).unavailable = !event.detail.resource.available;
    }
    requestAnimationFrame(paint);
  });
  window.addEventListener('pageshow', () => { void refreshAll(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden && !busy) void refreshAll(); });
  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-finora-review-bag]');
    if (!link) return;
    const drawer = document.querySelector('.fbg-native-cart-host cart-drawer-component');
    if (typeof drawer?.open === 'function') { event.preventDefault(); drawer.open(); void refreshAll(); }
  });
  window.FinoraCartUX = { paint, refresh: refreshAll, variantQuantity, productPresent };
  function init() { readSeed(); paint(); void refreshAll(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
